// v2 birch — a leaning birch: a kinked two-segment chalk-white trunk wearing
// dark bark flecks (tiny boxes hugging the stem), thin dark twigs, and a small
// airy crown of well-separated light-green lobes that let daylight through.
// Variant 1 grows as a twin-stem clump (a second slimmer trunk leaning away with
// its own small foliage); every variant re-leans the stem and re-scatters the
// flecks deterministically. Ported 1:1 from age-of-mcp's v2/tree-birch.ts, using
// the library's shared `tree` (h01, lobe, pick) helpers.
import * as THREE from "three";
import { defineModel, tree, type Part } from "../../lib";

const PALETTES = [
  { dark: "#5f9c3c", mid: "#7ab84e", lite: "#92cf63" },
  { dark: "#589640", mid: "#71b252", lite: "#8ac968" },
  { dark: "#66a038", mid: "#81bc4a", lite: "#9ad35e" },
] as const;

const BARK = "#eceadd";
const FLECK = "#2f2b26";
const TWIG = "#8a8576";

function birchParts(v: number): Part[] {
  const seed = 101 + v * 13;
  const lean = (tree.h01(seed, 0) - 0.5) * 0.16;
  const pal = tree.pick(PALETTES, v);
  const parts: Part[] = [
    // kinked two-segment chalk-white trunk
    { geometry: new THREE.CylinderGeometry(0.038, 0.055, 0.85, 6),
      color: BARK, position: [0, 0.425, 0], rotation: [0, 0, lean] },
    { geometry: new THREE.CylinderGeometry(0.026, 0.038, 0.7, 6),
      color: BARK, position: [-lean * 0.5, 1.1, 0], rotation: [0, 0, lean * 1.6] },
  ];
  // dark bark flecks hugging the stem
  for (let k = 0; k < 5; k++) {
    const fy = 0.2 + k * 0.18 + tree.h01(seed, 30 + k) * 0.08;
    const ang = tree.h01(seed, 40 + k) * Math.PI * 2;
    parts.push({
      geometry: new THREE.BoxGeometry(0.014, 0.05, 0.05),
      color: FLECK,
      position: [Math.cos(ang) * 0.042 - lean * fy * 0.4, fy, Math.sin(ang) * 0.042],
      rotation: [0, ang, 0],
    });
  }
  // thin dark twigs below the crown
  parts.push({ geometry: new THREE.CylinderGeometry(0.012, 0.018, 0.3, 4),
    color: TWIG, position: [-0.1, 1.28, 0.04], rotation: [0.3, 0, 0.9] });
  parts.push({ geometry: new THREE.CylinderGeometry(0.012, 0.018, 0.28, 4),
    color: TWIG, position: [0.1, 1.4, -0.04], rotation: [-0.25, 0, -0.95] });
  // small, airy, well-separated crown
  parts.push(tree.lobe(seed, 20, -0.2, 1.34, 0.1, 0.21, pal.dark, 0.62));
  parts.push(tree.lobe(seed, 21, 0.2, 1.42, -0.1, 0.2, pal.mid, 0.62));
  parts.push(tree.lobe(seed, 22, -0.04, 1.56, -0.06, 0.19, pal.mid, 0.65));
  parts.push(tree.lobe(seed, 23, 0.06, 1.66, 0.06, 0.17, pal.lite, 0.7));
  // variant 1: a twin-stem clump — second slimmer trunk leaning away
  if (v % 3 === 1) {
    parts.push({ geometry: new THREE.CylinderGeometry(0.028, 0.042, 0.95, 5),
      color: BARK, position: [0.16, 0.47, -0.06], rotation: [-0.06, 0, -0.22] });
    for (let k = 0; k < 2; k++) {
      const fy = 0.3 + k * 0.32 + tree.h01(seed, 80 + k) * 0.06;
      // hug the leaning twin stem: its axis tilts toward +x with height
      parts.push({
        geometry: new THREE.BoxGeometry(0.014, 0.045, 0.045),
        color: FLECK,
        position: [0.16 + (fy - 0.47) * 0.22 + 0.038, fy, -0.06],
        rotation: [0, tree.h01(seed, 84 + k) * Math.PI, 0],
      });
    }
    parts.push(tree.lobe(seed, 26, 0.3, 1.18, -0.1, 0.17, pal.dark, 0.62));
    parts.push(tree.lobe(seed, 27, 0.34, 1.3, -0.04, 0.15, pal.lite, 0.65));
  }
  return parts;
}

export const treeBirchV2 = defineModel({
  id: "tree-birch-v2",
  batchId: "fable",
  name: "Tree birch (v2)",
  tags: ["scenery","tree","birch","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tree birch.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.42,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => birchParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => birchParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => birchParts(2) },
  ],
  animations: [],
});
